import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateInviteRequest, Invite } from '@couch/shared';
import { useState } from 'react';
import { api, errorMessage } from '../../api/client';
import { Field, FormError, useSubmit } from '../../components/Field';

const INVITES_KEY = ['invites'] as const;
const VALIDITY_DAYS = [1, 3, 7, 14, 30] as const;
const formatDate = (iso: string) => new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' });

function CreatedLink({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { void navigator.clipboard.writeText(url).then(() => { setCopied(true); }); };
  return (
    <div role="status" className="created-link">
      <p><strong>This link is shown only now.</strong> Send it to the person you invite.</p>
      <div className="copy-row">
        <input readOnly value={url} aria-label="Invite link" onFocus={(e) => { e.target.select(); }} />
        <button type="button" onClick={copy}>{copied ? 'Copied' : 'Copy link'}</button>
      </div>
    </div>
  );
}

function CreateInviteForm() {
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const [days, setDays] = useState<number>(7);
  const [url, setUrl] = useState<string | null>(null);
  const request = (): CreateInviteRequest => (note.trim() ? { note: note.trim(), expiresInDays: days } : { expiresInDays: days });
  const form = useSubmit(() => api.createInvite(request()), (created) => {
    setUrl(created.url);
    setNote('');
    void queryClient.invalidateQueries({ queryKey: INVITES_KEY });
  });
  return (
    <form onSubmit={form.submit} noValidate className="invite-form">
      <Field label="Note (optional)" name="note" value={note} onChange={setNote} error={form.fieldErrors.note} hint="Who is it for? Only you see this." />
      <div className="field">
        <label htmlFor="invite-days">Valid for</label>
        <select id="invite-days" value={days} onChange={(e) => { setDays(Number(e.target.value)); }}>
          {VALIDITY_DAYS.map((d) => <option key={d} value={d}>{d === 1 ? '1 day' : `${d} days`}</option>)}
        </select>
      </div>
      <FormError message={form.formError} />
      <button type="submit" disabled={form.pending}>Create invite link</button>
      {url && <CreatedLink url={url} />}
    </form>
  );
}

function statusText(invite: Invite): string {
  return invite.status === 'used' && invite.usedBy ? `used by ${invite.usedBy.username}` : invite.status;
}

function InviteRow({ invite }: { invite: Invite }) {
  const queryClient = useQueryClient();
  const revoke = useMutation({
    mutationFn: () => api.revokeInvite(invite.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: INVITES_KEY }),
  });
  const label = `Revoke invite ${invite.note ?? `from ${formatDate(invite.createdAt)}`}`;
  return (
    <tr>
      <td>{invite.note ?? '—'}</td>
      <td><span className={`badge badge-${invite.status}`}>{statusText(invite)}</span></td>
      <td>{formatDate(invite.expiresAt)}</td>
      <td>
        {invite.status === 'pending' && (
          <button type="button" className="button-quiet" aria-label={label} disabled={revoke.isPending} onClick={() => { revoke.mutate(); }}>Revoke</button>
        )}
        {revoke.isError && <span role="alert" className="form-error">{errorMessage(revoke.error)}</span>}
      </td>
    </tr>
  );
}

function InvitesTable() {
  const invites = useQuery({ queryKey: INVITES_KEY, queryFn: api.listInvites });
  if (invites.isPending) return <p role="status">Loading invites…</p>;
  if (invites.isError) return <p role="alert" className="form-error">{errorMessage(invites.error)}</p>;
  if (invites.data.invites.length === 0) return <p className="muted">No invites yet.</p>;
  return (
    <div className="table-scroll">
      <table>
        <thead><tr><th scope="col">Note</th><th scope="col">Status</th><th scope="col">Expires</th><th scope="col"><span className="visually-hidden">Actions</span></th></tr></thead>
        <tbody>{invites.data.invites.map((invite) => <InviteRow key={invite.id} invite={invite} />)}</tbody>
      </table>
    </div>
  );
}

export function InvitesPanel() {
  return (
    <section className="panel" aria-labelledby="invites-title">
      <h2 id="invites-title">Invites</h2>
      <CreateInviteForm />
      <InvitesTable />
    </section>
  );
}
